"use client"

import { useState } from "react"
import { motion } from "motion/react"
import {
  GraduationCap,
  Gift,
  CalendarClock,
  Users,
  BadgeCheck,
  MessageCircle,
  Mail,
  Check,
} from "lucide-react"
import { useTranslation } from "@/components/language-provider"
import { fadeInUp, staggerContainer, scaleIn } from "@/lib/animations"
import { buildApplyWhatsapp, buildApplyMailto, CONTACT } from "@/lib/constants"
import type { Locale, FormStatus } from "@/types"

export default function BecomeClient() {
  const { t, locale } = useTranslation()
  const l = locale as Locale

  const [name, setName] = useState("")
  const [type, setType] = useState<"salon" | "individual">("salon")
  const [expertise, setExpertise] = useState("")
  const [city, setCity] = useState("")
  const [message, setMessage] = useState("")
  const [status, setStatus] = useState<FormStatus>("idle")

  const benefits = [
    { icon: Gift, title: t("becomeInstructor.benefit1Title"), desc: t("becomeInstructor.benefit1Desc") },
    { icon: CalendarClock, title: t("becomeInstructor.benefit2Title"), desc: t("becomeInstructor.benefit2Desc") },
    { icon: Users, title: t("becomeInstructor.benefit3Title"), desc: t("becomeInstructor.benefit3Desc") },
    { icon: BadgeCheck, title: t("becomeInstructor.benefit4Title"), desc: t("becomeInstructor.benefit4Desc") },
  ]

  const steps =
    l === "tr"
      ? ["Başvuru formunu doldur", "Ekibimiz seninle iletişime geçer", "Eğitimini yayınlarız", "Öğrenci başvurularını al"]
      : ["Fill in the application form", "Our team contacts you", "We publish your training", "Receive student applications"]

  function buildMessage(): string {
    const typeLabel =
      type === "salon" ? t("becomeInstructor.formTypeSalon") : t("becomeInstructor.formTypeIndividual")
    return l === "tr"
      ? `Eğitmen başvurusu\nAd: ${name}\nTür: ${typeLabel}\nUzmanlık: ${expertise}\nŞehir: ${city}\nMesaj: ${message}`
      : `Instructor application\nName: ${name}\nType: ${typeLabel}\nExpertise: ${expertise}\nCity: ${city}\nMessage: ${message}`
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !expertise.trim()) {
      setStatus("error")
      return
    }
    const text = buildMessage()
    const subject =
      l === "tr" ? "glossgo Akademi — Eğitmen Başvurusu" : "glossgo Akademi — Instructor Application"
    const wa = buildApplyWhatsapp(text)
    const target = wa ?? buildApplyMailto(subject, text)
    setStatus("success")
    window.open(target, wa ? "_blank" : "_self", wa ? "noopener,noreferrer" : undefined)
  }

  return (
    <div data-testid="beauty-academy-become-instructor-page">
      {/* Hero */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_-10%,rgba(168,85,247,0.08),transparent)]" />
        <motion.div
          className="relative mx-auto max-w-4xl px-4 text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={fadeInUp}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-sm font-medium text-purple-700"
          >
            <GraduationCap size={15} />
            {t("becomeInstructor.badge")}
          </motion.div>
          <motion.h1
            variants={fadeInUp}
            className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl md:text-5xl"
          >
            {t("becomeInstructor.title")}
          </motion.h1>
          <motion.p variants={fadeInUp} className="mx-auto mt-5 max-w-2xl text-gray-600 md:text-lg">
            {t("becomeInstructor.subtitle")}
          </motion.p>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="pb-4 md:pb-10">
        <div className="mx-auto max-w-7xl px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {benefits.map((b) => {
              const Icon = b.icon
              return (
                <motion.div
                  key={b.title}
                  variants={scaleIn}
                  className="rounded-2xl border border-gray-200 bg-white p-6"
                >
                  <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-purple-50">
                    <Icon size={24} className="text-purple-600" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{b.desc}</p>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* How + Form */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="mx-auto grid max-w-5xl gap-10 px-4 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">{t("becomeInstructor.howTitle")}</h2>
            <ol className="mt-6 space-y-5">
              {steps.map((step, i) => (
                <li key={step} className="flex items-start gap-4">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="pt-1 text-gray-700">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
            <h2 className="text-xl font-bold text-gray-900">{t("becomeInstructor.formTitle")}</h2>
            <form onSubmit={handleSubmit} className="mt-5 space-y-4" data-testid="beauty-academy-become-instructor-form">
              <Field label={t("becomeInstructor.formName")}>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="beauty-academy-become-instructor-name-input"
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                />
              </Field>

              <Field label={t("becomeInstructor.formType")}>
                <div className="flex gap-2">
                  {(["salon", "individual"] as const).map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => setType(opt)}
                      data-testid={`beauty-academy-become-instructor-type-option-${opt}`}
                      className={
                        "flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors " +
                        (type === opt
                          ? "border-gray-900 bg-gray-900 text-white"
                          : "border-gray-200 bg-white text-gray-600 hover:border-purple-300")
                      }
                    >
                      {opt === "salon"
                        ? t("becomeInstructor.formTypeSalon")
                        : t("becomeInstructor.formTypeIndividual")}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label={t("becomeInstructor.formExpertise")}>
                <input
                  value={expertise}
                  onChange={(e) => setExpertise(e.target.value)}
                  data-testid="beauty-academy-become-instructor-expertise-input"
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                />
              </Field>

              <Field label={t("becomeInstructor.formCity")}>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid="beauty-academy-become-instructor-city-input"
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                />
              </Field>

              <Field label={t("becomeInstructor.formMessage")}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder={t("becomeInstructor.formMessagePlaceholder")}
                  data-testid="beauty-academy-become-instructor-message-input"
                  className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                />
              </Field>

              {status === "error" ? (
                <p className="text-xs font-medium text-red-500" data-testid="beauty-academy-become-instructor-form-error">
                  {t("becomeInstructor.formMissing")}
                </p>
              ) : null}
              {status === "success" ? (
                <p
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-600"
                  data-testid="beauty-academy-become-instructor-form-success"
                >
                  <Check size={13} />
                  {t("becomeInstructor.formNote")}
                </p>
              ) : null}

              <button
                type="submit"
                data-testid="beauty-academy-become-instructor-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#ee6dfd] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#d376fb] hover:shadow-md"
              >
                {CONTACT.whatsapp ? <MessageCircle size={16} /> : <Mail size={16} />}
                {t("becomeInstructor.formSubmit")}
              </button>
              <p className="text-center text-[11px] leading-relaxed text-gray-500">{t("becomeInstructor.formNote")}</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-gray-900">{label}</span>
      {children}
    </label>
  )
}
